import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  ArrowLeft, Plus, Trash2, Bell, BellOff, Edit2, 
  TrendingUp, TrendingDown, Activity, Zap, RefreshCw, X
} from 'lucide-react';
import { Button } from '../components/ui/button';
import UpgradeModal from '../components/UpgradeModal';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const ALERT_TYPES = [
  { value: 'PRICE_CROSS', label: 'Price Cross', icon: TrendingUp, description: 'Alert when price crosses a threshold' },
  { value: 'PCT_MOVE', label: 'Percent Move', icon: Activity, description: 'Alert on significant price movement' },
  { value: 'ANOMALY_SCORE', label: 'Anomaly Score', icon: Zap, description: 'Alert when anomaly score exceeds threshold' },
  { value: 'NEW_ANOMALY', label: 'New Anomaly', icon: Bell, description: 'Alert when stock is detected as anomaly' }
];

const AlertsPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, isPro, token } = useAuth();
  
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingAlert, setEditingAlert] = useState(null);
  
  // Form state
  const [formSymbol, setFormSymbol] = useState(searchParams.get('symbol') || '');
  const [formType, setFormType] = useState('NEW_ANOMALY');
  const [formParams, setFormParams] = useState({});
  const [formCooldown, setFormCooldown] = useState(60);
  const [saving, setSaving] = useState(false);
  
  // Redirect free users
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!isPro) {
      setShowUpgradeModal(true);
    }
  }, [isAuthenticated, isPro, navigate]);
  
  // Open create modal if symbol in URL
  useEffect(() => {
    if (searchParams.get('symbol') && isPro) {
      setShowCreateModal(true);
    }
  }, [searchParams, isPro]);
  
  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    if (!isPro) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE}/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data.alerts || []);
    } catch (error) {
      if (error.response?.status === 403) {
        setShowUpgradeModal(true);
      } else {
        toast.error('Failed to load alerts');
      }
    } finally {
      setLoading(false);
    }
  }, [isPro, token]);
  
  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);
  
  // Reset form
  const resetForm = () => {
    setFormSymbol('');
    setFormType('NEW_ANOMALY');
    setFormParams({});
    setFormCooldown(60);
    setEditingAlert(null);
  };
  
  // Create/Update alert
  const handleSaveAlert = async (e) => {
    e.preventDefault();
    if (!formSymbol.trim()) {
      toast.error('Symbol is required');
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        symbol: formSymbol.toUpperCase(),
        type: formType,
        params: formParams,
        cooldownMinutes: formCooldown
      };
      
      if (editingAlert) {
        // Update existing
        await axios.patch(`${API_BASE}/alerts/${editingAlert.id}`, {
          params: formParams,
          cooldownMinutes: formCooldown
        }, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Alert updated');
      } else {
        // Create new
        await axios.post(`${API_BASE}/alerts`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Alert created');
      }
      
      setShowCreateModal(false);
      resetForm();
      fetchAlerts();
    } catch (error) {
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Failed to save alert');
      }
    } finally {
      setSaving(false);
    }
  };
  
  // Toggle alert active state
  const handleToggleAlert = async (alert) => {
    try {
      await axios.patch(`${API_BASE}/alerts/${alert.id}`, {
        isActive: !alert.isActive
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(alert.isActive ? 'Alert paused' : 'Alert activated');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to update alert');
    }
  };
  
  // Delete alert
  const handleDeleteAlert = async (alertId) => {
    if (!window.confirm('Delete this alert?')) return;
    
    try {
      await axios.delete(`${API_BASE}/alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Alert deleted');
      fetchAlerts();
    } catch (error) {
      toast.error('Failed to delete alert');
    }
  };
  
  // Edit alert
  const handleEditAlert = (alert) => {
    setEditingAlert(alert);
    setFormSymbol(alert.symbol);
    setFormType(alert.type);
    setFormParams(alert.params || {});
    setFormCooldown(alert.cooldownMinutes);
    setShowCreateModal(true);
  };
  
  // Handle upgrade modal close
  const handleUpgradeModalClose = () => {
    setShowUpgradeModal(false);
    if (!isPro) {
      navigate('/app');
    }
  };
  
  // Get params form based on alert type
  const renderParamsForm = () => {
    switch (formType) {
      case 'PRICE_CROSS':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Direction</label>
              <select
                value={formParams.direction || 'ABOVE'}
                onChange={(e) => setFormParams({ ...formParams, direction: e.target.value })}
                className="w-full px-3 py-2 rounded-lg text-sm appearance-none cursor-pointer"
                style={{
                  backgroundColor: '#27272a',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#f4f4f5'
                }}
              >
                <option value="ABOVE" style={{ backgroundColor: '#27272a', color: '#f4f4f5' }}>Crosses Above</option>
                <option value="BELOW" style={{ backgroundColor: '#27272a', color: '#f4f4f5' }}>Drops Below</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Target Price ($)</label>
              <input
                type="number"
                step="0.01"
                value={formParams.price || ''}
                onChange={(e) => setFormParams({ ...formParams, price: parseFloat(e.target.value) })}
                placeholder="100.00"
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: '#27272a',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#f4f4f5'
                }}
              />
            </div>
          </div>
        );
      
      case 'PCT_MOVE':
        return (
          <div className="space-y-3">
            <div>
              <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Direction</label>
              <select
                value={formParams.direction || 'ABS'}
                onChange={(e) => setFormParams({ ...formParams, direction: e.target.value })}
                className="w-full px-3 py-2 rounded-lg text-sm appearance-none cursor-pointer"
                style={{
                  backgroundColor: '#27272a',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#f4f4f5'
                }}
              >
                <option value="ABS" style={{ backgroundColor: '#27272a', color: '#f4f4f5' }}>Either Direction</option>
                <option value="UP" style={{ backgroundColor: '#27272a', color: '#f4f4f5' }}>Up Only</option>
                <option value="DOWN" style={{ backgroundColor: '#27272a', color: '#f4f4f5' }}>Down Only</option>
              </select>
            </div>
            <div>
              <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Percent Threshold (%)</label>
              <input
                type="number"
                step="0.5"
                min="0.5"
                max="100"
                value={formParams.percent || ''}
                onChange={(e) => setFormParams({ ...formParams, percent: parseFloat(e.target.value) })}
                placeholder="5"
                className="w-full px-3 py-2 rounded-lg text-sm"
                style={{
                  backgroundColor: '#27272a',
                  border: '1px solid rgba(255,255,255,0.08)',
                  color: '#f4f4f5'
                }}
              />
            </div>
          </div>
        );
      
      case 'ANOMALY_SCORE':
        return (
          <div>
            <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Minimum Score (1-100)</label>
            <input
              type="number"
              min="1"
              max="100"
              value={formParams.minScore || 50}
              onChange={(e) => setFormParams({ ...formParams, minScore: parseInt(e.target.value) })}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{
                backgroundColor: '#27272a',
                border: '1px solid rgba(255,255,255,0.08)',
                color: '#f4f4f5'
              }}
            />
          </div>
        );
      
      case 'NEW_ANOMALY':
      default:
        return (
          <p className="text-sm" style={{ color: '#71717a' }}>
            You'll be notified when this stock is detected as an anomaly.
          </p>
        );
    }
  };
  
  if (!isPro && showUpgradeModal) {
    return (
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={handleUpgradeModalClose}
        feature="Alerts"
      />
    );
  }
  
  return (
    <div className="min-h-screen" style={{ backgroundColor: '#09090b' }}>
      {/* Header */}
      <div className="border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/app')}
                className="p-2 rounded-lg transition-colors"
                style={{ color: '#71717a' }}
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-xl font-semibold" style={{ color: '#f4f4f5' }}>
                  My Alerts
                </h1>
                <p className="text-sm" style={{ color: '#71717a' }}>
                  {alerts.filter(a => a.isActive).length} active alert{alerts.filter(a => a.isActive).length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchAlerts}
                disabled={loading}
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button
                size="sm"
                onClick={() => { resetForm(); setShowCreateModal(true); }}
              >
                <Plus className="w-4 h-4 mr-2" />
                New Alert
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-12" style={{ color: '#71717a' }}>
            Loading alerts...
          </div>
        ) : alerts.length === 0 ? (
          <div 
            className="text-center py-12 rounded-xl"
            style={{ 
              backgroundColor: 'rgba(255,255,255,0.02)',
              border: '1px dashed rgba(255,255,255,0.1)'
            }}
          >
            <Bell className="w-12 h-12 mx-auto mb-4" style={{ color: '#3f3f46' }} />
            <h3 className="text-lg font-medium mb-2" style={{ color: '#a1a1aa' }}>
              No alerts yet
            </h3>
            <p className="mb-4" style={{ color: '#71717a' }}>
              Create an alert to get notified about stock movements
            </p>
            <Button onClick={() => setShowCreateModal(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Create Alert
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert) => {
              const typeInfo = ALERT_TYPES.find(t => t.value === alert.type);
              const TypeIcon = typeInfo?.icon || Bell;
              
              return (
                <div
                  key={alert.id}
                  className={`p-4 rounded-xl flex items-center justify-between ${!alert.isActive ? 'opacity-50' : ''}`}
                  style={{
                    backgroundColor: 'rgba(255,255,255,0.02)',
                    border: `1px solid ${alert.isActive ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.03)'}`
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center"
                      style={{
                        backgroundColor: alert.isActive ? 'rgba(124,92,255,0.1)' : 'rgba(255,255,255,0.05)',
                        color: alert.isActive ? '#a78bfa' : '#52525b'
                      }}
                    >
                      <TypeIcon className="w-5 h-5" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium" style={{ color: '#f4f4f5' }}>
                          {alert.symbol}
                        </span>
                        <span 
                          className="text-xs px-2 py-0.5 rounded"
                          style={{
                            backgroundColor: 'rgba(255,255,255,0.05)',
                            color: '#a1a1aa'
                          }}
                        >
                          {typeInfo?.label || alert.type}
                        </span>
                      </div>
                      <p className="text-sm mt-0.5" style={{ color: '#71717a' }}>
                        {alert.type === 'PRICE_CROSS' && `${alert.params?.direction === 'ABOVE' ? 'Crosses above' : 'Drops below'} $${alert.params?.price}`}
                        {alert.type === 'PCT_MOVE' && `${alert.params?.percent}% move ${alert.params?.direction === 'ABS' ? 'either direction' : alert.params?.direction?.toLowerCase()}`}
                        {alert.type === 'ANOMALY_SCORE' && `Score >= ${alert.params?.minScore}`}
                        {alert.type === 'NEW_ANOMALY' && 'Alert on new anomaly detection'}
                        {' • '}{alert.cooldownMinutes}min cooldown
                      </p>
                      {alert.lastTriggeredAt && (
                        <p className="text-xs mt-1" style={{ color: '#52525b' }}>
                          Last triggered: {new Date(alert.lastTriggeredAt).toLocaleString()}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleToggleAlert(alert)}
                      title={alert.isActive ? 'Pause alert' : 'Activate alert'}
                    >
                      {alert.isActive ? (
                        <BellOff className="w-4 h-4" />
                      ) : (
                        <Bell className="w-4 h-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleEditAlert(alert)}
                      title="Edit alert"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteAlert(alert.id)}
                      className="text-red-400 hover:text-red-300"
                      title="Delete alert"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      
      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: 'rgba(0, 0, 0, 0.7)', backdropFilter: 'blur(4px)' }}
          onClick={() => { setShowCreateModal(false); resetForm(); }}
        >
          <div 
            className="w-full max-w-md rounded-xl p-6"
            style={{
              backgroundColor: '#18181b',
              border: '1px solid rgba(255,255,255,0.08)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold" style={{ color: '#f4f4f5' }}>
                {editingAlert ? 'Edit Alert' : 'Create Alert'}
              </h2>
              <button
                onClick={() => { setShowCreateModal(false); resetForm(); }}
                style={{ color: '#71717a' }}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <form onSubmit={handleSaveAlert} className="space-y-4">
              {/* Symbol */}
              <div>
                <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Symbol</label>
                <input
                  type="text"
                  value={formSymbol}
                  onChange={(e) => setFormSymbol(e.target.value.toUpperCase())}
                  placeholder="AAPL"
                  disabled={!!editingAlert}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: editingAlert ? 'rgba(255,255,255,0.02)' : '#27272a',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#f4f4f5'
                  }}
                  maxLength={10}
                  required
                />
              </div>
              
              {/* Alert Type */}
              <div>
                <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>Alert Type</label>
                <select
                  value={formType}
                  onChange={(e) => { setFormType(e.target.value); setFormParams({}); }}
                  disabled={!!editingAlert}
                  className="w-full px-3 py-2 rounded-lg text-sm appearance-none cursor-pointer"
                  style={{
                    backgroundColor: editingAlert ? 'rgba(255,255,255,0.02)' : '#27272a',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#f4f4f5'
                  }}
                >
                  {ALERT_TYPES.map((type) => (
                    <option 
                      key={type.value} 
                      value={type.value}
                      style={{ backgroundColor: '#27272a', color: '#f4f4f5' }}
                    >
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Type-specific params */}
              <div 
                className="p-3 rounded-lg"
                style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}
              >
                {renderParamsForm()}
              </div>
              
              {/* Cooldown */}
              <div>
                <label className="block text-sm mb-1.5" style={{ color: '#a1a1aa' }}>
                  Cooldown (minutes between alerts)
                </label>
                <input
                  type="number"
                  min="5"
                  max="1440"
                  value={formCooldown}
                  onChange={(e) => setFormCooldown(parseInt(e.target.value))}
                  className="w-full px-3 py-2 rounded-lg text-sm"
                  style={{
                    backgroundColor: '#27272a',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#f4f4f5'
                  }}
                />
              </div>
              
              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                >
                  Cancel
                </Button>
                <Button type="submit" className="flex-1" disabled={saving}>
                  {saving ? 'Saving...' : (editingAlert ? 'Update' : 'Create')}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      <UpgradeModal 
        isOpen={showUpgradeModal && isPro === false} 
        onClose={handleUpgradeModalClose}
        feature="Alerts"
      />
    </div>
  );
};

export default AlertsPage;
